export interface FloatingTabBarItem {
  key: string;
  /** Tabler icon name (without `ti-` prefix). */
  icon: string;
  label: string;
  onClick: () => void;
  /** Small --gold notification dot on the icon (e.g. unread message). */
  dot?: boolean;
}

export interface FloatingTabBarProps {
  items: FloatingTabBarItem[];
  /** Key of the active item. */
  active: string;
  /**
   * A lighter sheet sits right above the bar (a question sheet): fades the band
   * behind the bar from the sheet's colour at the top to the page background at
   * the bottom, so the sheet melts into the screen instead of ending in an edge.
   */
  fade?: boolean;
}

/**
 * FloatingTabBar — mobile-only primary nav rendered as a floating rounded
 * pill docked at the bottom of the screen (not position:fixed — a normal
 * flex sibling at the end of the layout column, so it never overlaps
 * content like a chat composer sitting above it). Pair with `md:hidden`
 * at the call site if the parent doesn't already scope it to mobile.
 */
export function FloatingTabBar({ items, active, fade = false }: FloatingTabBarProps) {
  return (
    <nav className="shrink-0 px-4 pt-1 pb-safe-or-4" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      {/* The band behind the bar, fading in and out with the sheet above it. */}
      <div
        aria-hidden="true"
        data-testid="floating-tab-bar-fade"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, var(--surface), var(--bg))',
          opacity: fade ? 1 : 0, transition: 'opacity 200ms ease-out',
        }}
      />
      <div
        style={{
          position: 'relative',
          display: 'flex', gap: 2, width: '100%', maxWidth: 420,
          background: 'var(--sidebar-bg)', border: '1px solid var(--line)',
          borderRadius: 'var(--r-pill)', boxShadow: '0 8px 28px rgba(0,0,0,.14)',
          padding: 6,
        }}
      >
        {items.map((item) => {
          const isActive = item.key === active;
          return (
            <button className="sk-hit"
              key={item.key}
              type="button"
              onClick={item.onClick}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              style={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 3, padding: '8px 4px', border: 'none',
                borderRadius: 'var(--r-pill)', cursor: 'pointer',
                background: isActive ? 'var(--nav-active-bg)' : 'transparent',
                transition: 'background .15s, color .15s',
              }}
            >
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <i className={`ti ti-${item.icon}`} style={{ fontSize: 'calc(19px * var(--text-scale))', color: isActive ? 'var(--accent)' : 'var(--text-dim)' }} />
                {item.dot && (
                  <span
                    style={{
                      position: 'absolute', top: -2, right: -3, width: 7, height: 7, borderRadius: '50%',
                      background: 'rgb(var(--gold))', border: '1.5px solid var(--sidebar-bg)',
                    }}
                  />
                )}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 'calc(10px * var(--text-scale))', fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--text)' : 'var(--text-dim)', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default FloatingTabBar;
